import { useCallback, useEffect, useState } from 'react';
import { useSupabase } from '../../contexts/SupabaseContext';

const storageKey = (tourId, userId) => `mickles-tour-${tourId}:${userId || 'guest'}`;

const read = (storage, key) => {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
};

const write = (storage, key) => {
  try {
    storage.setItem(key, 'done');
  } catch {
    // Storage unavailable (private mode): the guide may show again next visit
  }
};

// This guide is turned off for good ("Don't show this guide again", or finished)
export const isGuideHidden = (key) => read(localStorage, key) === 'done';

// Controls one page's guided tour. It opens by itself when the user visits the page
// (once `ready` is true, i.e. the page has rendered its data) and can be reopened
// any time with `start()` (the Guide button).
//
// How the guide is closed decides whether it opens by itself again:
//   'finish' (Done on the last step) or 'never' (Don't show this guide again)
//       → never again for this user on this device (per guide, so other pages are unaffected)
//   'skip' (Skip tour, ✕, Esc)
//       → not again in this browser session; it opens again on a later visit
const useTour = (tourId, { ready = true } = {}) => {
  const { user } = useSupabase();
  const key = storageKey(tourId, user?.id);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!ready || isGuideHidden(key) || read(sessionStorage, key) === 'done') return undefined;
    // Small delay so the page settles (fonts, layout) before highlighting elements
    const timer = setTimeout(() => setOpen(true), 600);
    return () => clearTimeout(timer);
  }, [ready, key]);

  const start = useCallback(() => setOpen(true), []);

  const close = useCallback((reason = 'skip') => {
    setOpen(false);
    if (reason === 'finish' || reason === 'never') write(localStorage, key);
    else write(sessionStorage, key);
  }, [key]);
  // GuidedTour reads this to tell whether this guide is already turned off
  close.guideKey = key;

  return { open, start, close };
};

export default useTour;
